import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import {
  MemberResponse,
  MembersHttpService,
  PermissionResponse,
  WorkspaceRequest,
  WorkspacesHttpService,
} from '@sentry/api-resources';
import { AuthConfig, OAuthService } from 'angular-oauth2-oidc';
import { BehaviorSubject, Observable } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { Workspace } from './workspace.model';

const workspaceSessionKey = 'workspace';

@Injectable({
  providedIn: 'root',
})
export class UserContextService {
  private authUrl = '';
  private clientId = '';

  private readonly _member$ = new BehaviorSubject<MemberResponse | null>(null);
  readonly member$ = this._member$.asObservable();

  private readonly _permissions$ = new BehaviorSubject<PermissionResponse[]>(
    []
  );
  readonly permissions$ = this._permissions$.asObservable();

  private readonly _workspaces$ = new BehaviorSubject<Workspace[]>([]);
  readonly workspaces$ = this._workspaces$.asObservable();

  private readonly _selectedWorkspace$ = new BehaviorSubject<Workspace | null>(
    null
  );
  readonly selectedWorkspace$ = this._selectedWorkspace$;

  constructor(
    private readonly workspaceHttpService: WorkspacesHttpService,
    private readonly membersHttpService: MembersHttpService,
    private readonly oauthService: OAuthService
  ) {}

  initializeOAuth(issuer: string, clientId: string, redirectUri: string): void {
    this.authUrl = issuer;
    this.clientId = clientId;
    const authCodeFlowConfig: AuthConfig = {
      issuer,
      redirectUri,
      clientId,
      responseType: 'code',
      scope: 'openid profile email api',
      useSilentRefresh: false,
      showDebugInformation: true,
      sessionChecksEnabled: false,
      timeoutFactor: 0.01,
      clearHashAfterLogin: true,
      customQueryParams: {
        audience: 'https://localhost:5001',
      },
    };

    this.oauthService.configure(authCodeFlowConfig);
    this.oauthService.loadDiscoveryDocumentAndTryLogin();
  }

  tryLogin() {
    this.oauthService.initCodeFlow();
  }

  getEmailAddress() {
    return this.oauthService.getIdentityClaims()?.['email'] ?? null;
  }

  loadWorkspaces = (): Observable<Workspace[]> =>
    this.workspaceHttpService
      .get()
      .pipe(
        tap((workspaces) =>
          this._workspaces$.next(
            workspaces.sort((a, b) =>
              a.name.toLowerCase().localeCompare(b.name.toLowerCase())
            )
          )
        )
      );

  selectWorkspace(workspace: Workspace) {
    this._permissions$.next([]);

    sessionStorage.setItem(workspaceSessionKey, workspace.id);
    this._selectedWorkspace$.next(workspace);

    return this.membersHttpService.getLoggedInMember().pipe(
      switchMap((member) => {
        this._member$.next(member);

        return this.membersHttpService.getPermissions(member.id);
      }),
      tap((permissions) => {
        this._permissions$.next(permissions);
      })
    );
  }

  createWorkspace(request: WorkspaceRequest) {
    return this.workspaceHttpService
      .post(request)
      .pipe(switchMap(() => this.loadWorkspaces()));
  }

  deleteWorkspace(workspaceId: string) {
    return this.workspaceHttpService.delete(workspaceId);
  }

  editWorkspace(workspaceId: string, workspaceRequest: WorkspaceRequest) {
    return this.workspaceHttpService
      .put(workspaceId, workspaceRequest)
      .pipe(tap((workspace) => this._selectedWorkspace$.next(workspace)));
  }

  getSelectedWorkspace() {
    return this._selectedWorkspace$.value;
  }

  navigateToWorkspaceSelectionPage(router: Router) {
    sessionStorage.removeItem(workspaceSessionKey);
    this._selectedWorkspace$.next(null);
    router.navigate(['workspace', 'selection']);
  }

  getPermissions = () => this._permissions$.value;

  workspaceCodeExists(code: string) {
    return this.workspaceHttpService.exists(code);
  }

  removePermissions() {
    this._permissions$.next([]);
  }

  getAccessToken(): string | null {
    return this.oauthService.getAccessToken();
  }

  hasValidAccessToken(): boolean {
    return this.oauthService.hasValidAccessToken();
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  waitForIdentityClaims(): Observable<any> {
    return new Observable((observer) => {
      const checkAndEmitClaims = () => {
        const claims = this.oauthService.getIdentityClaims();
        if (claims && this.oauthService.hasValidAccessToken()) {
          observer.next(claims);
          observer.complete();
          return true;
        }
        return false;
      };

      // Initial check in case the claims and token are already valid
      if (checkAndEmitClaims()) {
        return;
      }

      // Listen for OAuth events - either success or failure
      const eventsSubscription = this.oauthService.events.subscribe((event) => {
        if (
          event.type === 'token_received' ||
          event.type === 'token_refreshed'
        ) {
          checkAndEmitClaims();
        } else if (
          event.type === 'token_error' ||
          event.type === 'discovery_document_load_error'
        ) {
          observer.error(new Error('Authentication failed'));
          this.tryLogin();
        }
      });

      // Cleanup on unsubscribe
      return () => eventsSubscription.unsubscribe();
    });
  }

  logout() {
    this.oauthService.logOut(true);
    sessionStorage.clear();
    localStorage.clear();

    const logoutUrl = `${this.authUrl}v2/logout?client_id=${
      this.clientId
    }&returnTo=${encodeURIComponent(window.location.origin + '/login')}`;
    window.location.href = logoutUrl;
  }
}
